import React, { useState, useCallback } from 'react';
import type { ContinuousAuthStatus } from '@/types/auth';
import { useWebSocket } from '@/hooks/useWebSocket';

interface ContinuousAuthWidgetProps {
  /** Override status (otherwise uses internal WebSocket) */
  status?: ContinuousAuthStatus;
  /** Override score */
  score?: number | null;
  /** Whether the widget is expanded by default */
  defaultExpanded?: boolean;
}

const statusConfig: Record<ContinuousAuthStatus, {
  color: string;
  bgColor: string;
  label: string;
  description: string;
}> = {
  active: {
    color: '#00d26a',
    bgColor: 'bg-accent-success',
    label: 'Verificado',
    description: 'Autenticacao continua ativa',
  },
  warning: {
    color: '#f5a623',
    bgColor: 'bg-accent-warning',
    label: 'Atencao',
    description: 'Padrao de digitacao divergente',
  },
  degraded: {
    color: '#e74c3c',
    bgColor: 'bg-accent-danger',
    label: 'Degradado',
    description: 'Verificacao adicional necessaria',
  },
  disconnected: {
    color: '#646681',
    bgColor: 'bg-txt-muted',
    label: 'Desconectado',
    description: 'Sem conexao com o servidor',
  },
};

/**
 * ContinuousAuthWidget — Fixed widget in the bottom-right corner
 * showing continuous authentication status with a colored dot.
 */
export default function ContinuousAuthWidget({
  status: externalStatus,
  score: externalScore,
  defaultExpanded = false,
}: ContinuousAuthWidgetProps) {
  const ws = useWebSocket({ autoConnect: !externalStatus });
  const status = externalStatus ?? ws.status;
  const score = externalScore ?? ws.currentScore;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const config = statusConfig[status];

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <div className="fixed bottom-4 right-4 z-40">
      {/* Expanded panel */}
      {expanded && (
        <div className="mb-2 w-64 p-4 bg-bg-secondary/95 backdrop-blur-sm border border-bg-surface rounded-card shadow-xl animate-slide-up">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-txt-primary font-sans">
              Auth Continua
            </h4>
            <button
              onClick={toggleExpanded}
              className="text-txt-muted hover:text-txt-primary transition-colors text-sm"
            >
              &times;
            </button>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 mb-3">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: config.color }}
            />
            <span className="text-sm font-medium" style={{ color: config.color }}>
              {config.label}
            </span>
          </div>

          <p className="text-xs text-txt-muted font-sans mb-3">
            {config.description}
          </p>

          {/* Score */}
          {score !== null && score !== undefined && (
            <div className="flex items-center justify-between p-2 bg-bg-surface/50 rounded-lg">
              <span className="text-xs text-txt-muted font-sans">Score</span>
              <span className="text-sm font-mono font-bold" style={{ color: config.color }}>
                {Math.round(score)}%
              </span>
            </div>
          )}

          {/* Connection info */}
          <div className="mt-3 pt-3 border-t border-bg-surface">
            <div className="flex items-center justify-between text-xs text-txt-muted">
              <span>Status da conexao</span>
              <span className="capitalize">{status}</span>
            </div>
          </div>
        </div>
      )}

      {/* Floating dot button */}
      <button
        onClick={toggleExpanded}
        className={`
          w-10 h-10 rounded-full flex items-center justify-center
          bg-bg-secondary/95 backdrop-blur-sm border border-bg-surface
          shadow-lg hover:shadow-xl transition-all duration-200
          hover:scale-105 ml-auto
        `}
        title={`Auth Continua: ${config.label}`}
      >
        <span className="relative flex h-3 w-3">
          {status === 'active' && (
            <span
              className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-50"
              style={{ backgroundColor: config.color }}
            />
          )}
          <span
            className="relative inline-flex rounded-full h-3 w-3"
            style={{
              backgroundColor: config.color,
              boxShadow: `0 0 8px ${config.color}60`,
            }}
          />
        </span>
      </button>
    </div>
  );
}
